import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'app-popup-instructions',
  imports: [CommonModule],
  standalone: true,
  template: `
    <div class="popup-help">
      <h2 class="title">Pop-up was blocked</h2>
      <p class="lead">
        Your browser prevented a new tab from opening. Follow these steps, then try again.
      </p>

      <ng-container [ngSwitch]="browser">
        <!-- Chrome / Edge (Chromium) path -->
        <div *ngSwitchCase="'chromium'">
          <ol class="steps">
            <li>
              Look for the
              <strong>Blocked pop-up</strong> icon near the right side of the address bar:
              <div class="img-wrap">
                <img src="/images/popup-tutorial/popup-edge-1.png" alt="Blocked pop-up icon" />
              </div>
            </li>
            <li>
              Click it, then select
              <strong>Always allow pop-ups and redirects from
                {{ siteToAllow || defaultSite }}</strong>.
              <div class="img-wrap">
                <img src="/images/popup-tutorial/popup-edge-2.png" width="10rem" alt="Always allow pop-ups and redirects dialog" />
              </div>
            </li>
            <li>
              Click <strong>Done</strong>, then use the button below to open the tab again.
            </li>
          </ol>
        </div>

        <!-- Firefox path -->
        <div *ngSwitchCase="'firefox'">
          <ol class="steps">
            <li>
              Look for the <strong>pop-up blocked</strong> banner under the address bar.
            </li>
            <li>
              Click <strong>Options</strong> (or the settings icon) and choose
              <strong>Allow pop-ups for {{ siteToAllow || defaultSite }}</strong>.
            </li>
            <li>Use the button below to open the tab again.</li>
          </ol>
        </div>

        <!-- Safari path -->
        <div *ngSwitchCase="'safari'">
          <ol class="steps">
            <li>
              In the menu bar, go to <strong>Safari → Settings → Websites → Pop-up Windows</strong>.
            </li>
            <li>
              Find <strong>{{ siteToAllow || defaultSite }}</strong> in the list and set it to
              <strong>Allow</strong>.
            </li>
            <li>Come back and use the button below to open the tab again.</li>
          </ol>
        </div>

        <!-- Fallback (unknown browser) -->
        <div *ngSwitchDefault>
          <ol class="steps">
            <li>Open your browser’s site settings for <strong>{{ siteToAllow || defaultSite }}</strong>.</li>
            <li>Find the <strong>Pop-ups and redirects</strong> setting and set it to <strong>Allow</strong>.</li>
            <li>Use the button below to try opening the tab again.</li>
          </ol>
        </div>
      </ng-container>

      <div class="actions flex flex-col">
        <button type="button" class="btn" (click)="retry.emit()">Try again</button>
        <a *ngIf="fallbackHref"
           class="link"
           [href]="fallbackHref"
           target="_blank"
           rel="noopener">Open in a new tab</a>
      </div>

      <details class="notes">
        <summary>Why this happens</summary>
        <p>
          Browsers block tabs opened outside a direct user action (like a click).
          After you allow pop-ups for this site, or use the button above (a user gesture),
          the new tab should open reliably.
        </p>
      </details>
    </div>
  `,
  styles: [`
    .popup-help { max-width: 500px; margin: 0 auto; padding: 1rem; font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; }
    .title { margin: 0 0 .25rem; font-size: 1.25rem; }
    .lead { color: #555; margin: 0 0 1rem; }
    .steps { padding-left: 1.2rem; margin: 0 0 1rem; }
    .steps li { margin: .5rem 0; }
    .img-wrap { margin: .5rem 0; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; background: #fafafa; }
    .img-wrap img { display: block; width: 100%; height: auto; }
    .actions { display: flex; gap: .75rem; align-items: center; margin-top: .75rem; }
    .btn { border: 1px solid #999; background: #f6f6f6; padding: .5rem .9rem; border-radius: 8px; cursor: pointer; }
    .btn:hover { background: #eee; }
    .link { text-decoration: underline; color: #0b57d0; }
    .notes { margin-top: .75rem; color: #666; }
  `]
})
export class PopupInstructionsComponent {
  /** Domain/site to display in the instructions (“Allow pop-ups for …”) */
  @Input() siteToAllow: string | null = null;

  /** Optional direct link to offer as a manual open fallback (target=_blank). */
  @Input() fallbackHref: string | null = null;

  /** Emit when user clicks “Try again”. Parent should attempt open in click handler. */
  @Output() retry = new EventEmitter<void>();

  get defaultSite(): string {
    // Show something helpful even if not provided
    try {
      return location.host || location.origin || 'this site';
    } catch {
      return 'this site';
    }
  }

  /** Rudimentary browser detection just to pick the right copy. */
  get browser(): 'chromium' | 'firefox' | 'safari' | 'other' {
    const ua = navigator.userAgent || '';
    if (/Edg\/|Chrome\/|Chromium\/|Brave\//i.test(ua)) return 'chromium';
    if (/Firefox\//i.test(ua)) return 'firefox';
    // iOS Safari user agent often includes "Safari" without Chrome/Firefox tokens
    if (/Safari\/(?!.*Chrome)/i.test(ua) || /iPhone|iPad|Macintosh/.test(ua) && /Version\/.+ Safari\//.test(ua)) return 'safari';
    return 'other';
  }
}
